import React, { useState } from 'react';
import { Receipt, ArrowDownCircle, ChevronDown, ChevronUp, DollarSign, PieChart } from 'lucide-react';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент для отображения детальной разбивки визовых сборов
 */
const FeeBreakdown = ({ feeBreakdown, results, exchangeRates, preferredCurrency = 'rub' }) => {
  const [showDetails, setShowDetails] = useState(false);

  if (!feeBreakdown || feeBreakdown.length === 0 || !results) {
    return null;
  }

  // Функция для конвертации валюты в предпочитаемую
  const convertCurrency = (amount, fromCurrency) => {
    // Сначала конвертируем в рубли
    let amountInRub = amount;
    if (fromCurrency !== 'RUB') {
      amountInRub = amount * exchangeRates[fromCurrency];
    }

    // Затем конвертируем в предпочитаемую валюту
    if (preferredCurrency === 'rub') {
      return { value: amountInRub, symbol: '₽' };
    } else if (preferredCurrency === 'usd') {
      return { value: amountInRub / exchangeRates.USD, symbol: '$' };
    } else if (preferredCurrency === 'eur') {
      return { value: amountInRub / exchangeRates.EUR, symbol: '€' };
    }

    // По умолчанию возвращаем в рублях
    return { value: amountInRub, symbol: '₽' };
  };

  // Рассчитываем процентное соотношение для каждого сбора
  const totalCost = results.totalRubCost;
  const feeBreakdownWithPercentage = feeBreakdown.map(fee => {
    let feeInRub = fee.amount;
    if (fee.currency !== 'RUB') {
      feeInRub = fee.amount * exchangeRates[fee.currency];
    }
    
    const percentage = (feeInRub / totalCost) * 100;
    
    return {
      ...fee,
      amountInRub: feeInRub,
      percentage: Math.round(percentage * 10) / 10 // Округление до 1 десятичного знака
    };
  });

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Receipt className="w-5 h-5 mr-2 text-purple-500" />
          Подробная разбивка визовых сборов
        </h3>

        <button
          onClick={() => setShowDetails(!showDetails)}
          className="text-sm flex items-center text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 transition-colors"
          aria-expanded={showDetails}
        >
          {showDetails ? (
            <>
              <ChevronUp className="w-4 h-4 mr-1" />
              Скрыть детали
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4 mr-1" />
              Показать детали
            </>
          )}
        </button>
      </div>

      {/* Визуализация разбивки */}
      <div className="mt-4 mb-6 overflow-hidden rounded-lg bg-gray-50 dark:bg-gray-900/30">
        <div className="flex h-6 w-full">
          {feeBreakdownWithPercentage.map((fee, index) => (
            <div
              key={index}
              className="h-full transition-all duration-500"
              style={{
                width: `${fee.percentage}%`,
                backgroundColor: getColorForFeeType(fee.name),
              }}
              title={`${fee.name}: ${fee.percentage}%`}
            ></div>
          ))}
        </div>
      </div>

      {/* Легенда */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        {feeBreakdownWithPercentage.map((fee, index) => (
          <div key={index} className="flex items-center">
            <div
              className="w-3 h-3 rounded-sm mr-2 flex-shrink-0"
              style={{ backgroundColor: getColorForFeeType(fee.name) }}
            ></div>
            <div className="flex-grow text-sm text-gray-600 dark:text-gray-400">{fee.name}</div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-300">
              {fee.percentage}%
            </div>
          </div>
        ))}
      </div>

      {/* Детальная таблица сборов */}
      {showDetails && (
        <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Наименование
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Сумма
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Процент
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Описание
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {feeBreakdownWithPercentage.map((fee, index) => {
                const convertedAmount = convertCurrency(fee.amount, fee.currency);
                
                return (
                  <tr key={index}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div
                          className="w-3 h-3 rounded-sm mr-2"
                          style={{ backgroundColor: getColorForFeeType(fee.name) }}
                        ></div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {fee.name}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                      <div>
                        {formatCurrency(fee.amount)} {fee.currency === 'RUB' ? '₽' : fee.currency}
                      </div>
                      {fee.currency !== 'RUB' && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          ≈ {formatCurrency(fee.amountInRub)} ₽
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                      {fee.percentage}%
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {fee.description}
                    </td>
                  </tr>
                );
              })}
              {/* Итоговая строка */}
              <tr className="bg-gray-50 dark:bg-gray-700">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 dark:text-white">
                  ИТОГО:
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-bold text-gray-900 dark:text-white">
                  {formatCurrency(results.totalRubCost)} ₽
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-bold text-gray-900 dark:text-white">
                  100%
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                  Общая стоимость визы
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800">
        <div className="flex items-start gap-3">
          <DollarSign className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5" />
          <div>
            <p className="text-sm text-purple-700 dark:text-purple-300">
              Цены действительны на сегодняшний день. Визовые сборы могут изменяться в зависимости от решения консульств и визовых центров. Рекомендуем уточнять актуальные размеры сборов на официальных сайтах посольств и визовых центров перед подачей документов.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Функция для получения цвета для типа сбора
const getColorForFeeType = (feeName) => {
  const colorMap = {
    'Консульский сбор': '#8b5cf6', // purple-500
    'Сервисный сбор визового центра': '#f59e0b', // amber-500
    'Туристическая страховка': '#10b981', // emerald-500
    'Перевод документов': '#06b6d4', // cyan-500
    'Доставка документов': '#f97316', // orange-500
    'Биометрия': '#0ea5e9', // sky-500
    'Фотографии': '#ec4899', // pink-500
    'Дополнительные услуги': '#6366f1', // indigo-500
  };

  return colorMap[feeName] || '#9ca3af'; // gray-400 по умолчанию
};

export default FeeBreakdown;